import { useCallback, useEffect, useState } from 'react';
import type { AdminListResponse, AdminWhatsappInstanceRow } from '../../../domain/superadmin/types';
import {
  EMPTY_WHATSAPP_FILTERS,
  type AdminWhatsappFilters,
} from '../../../domain/superadmin/whatsappFilters';
import { useAppContext } from '../../../contexts/AppContext';
import { fetchAdminWhatsappInstances } from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

const PAGE_SIZE = 25;

export type { AdminWhatsappFilters };
export { EMPTY_WHATSAPP_FILTERS };

export function useAdminWhatsappInstances() {
  const [data, setData] = useState<AdminListResponse<AdminWhatsappInstanceRow> | null>(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState<AdminWhatsappFilters>(EMPTY_WHATSAPP_FILTERS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setCurrentPage } = useAppContext();

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAdminWhatsappInstances({
        page,
        pageSize: PAGE_SIZE,
        q: q || undefined,
        statusBucket: filters.statusBucket || undefined,
        provider: filters.provider || undefined,
      });
      setData(result);
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        setCurrentPage('dashboard');
        notify.warning('Acesso não autorizado à área de superadmin.');
        return;
      }
      setError(e instanceof Error ? e.message : 'Erro ao carregar instâncias WhatsApp');
    } finally {
      setIsLoading(false);
    }
  }, [page, q, filters, setCurrentPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchFilters = useCallback((patch: Partial<AdminWhatsappFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  return {
    data,
    isLoading,
    error,
    refetch: load,
    page,
    setPage,
    q,
    setQ,
    filters,
    patchFilters,
    pageSize: PAGE_SIZE,
  };
}
